import SDFormControl from '@/shared/components/SDComponentsNewDesign/SDFormControl/SDFormControl'
import { ModalButton } from 'baseui/modal'
import { ArrowRight } from 'baseui/icon'
import { Spinner } from 'baseui/spinner'
import { useTranslation } from 'react-i18next'
import { useStyletron } from 'baseui'
import { SIZE } from 'baseui/input'
import usePdlSearchV2 from './usePdlSearchV2'
import useRetrievePDLsV2 from './useRetrievePDLsV2'
import SDInput from '@/shared/components/SDComponentsNewDesign/SDInput/SDInput'
import SDButtonGroup from '@/shared/components/SDComponents/SDButtonGroup/SDButtonGroup'
import SDButtonGroupButton from '@/shared/components/SDComponents/SDButtonGroup/SDButtonGroupButton'
import AddressSelect from '@/containers/SubscriptionFormPageNew/Components/shared/AddressSelect/AddressSelect'
import {
  SearchModalBody,
  SearchModalFooter,
} from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSearch/styled-components'
import useMeterSearchField from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSearch/useMeterSearchField'
import { pulseModalButtonOverrides } from '@/containers/SubscriptionFormPageNew/Components/shared/MeterSearch/styled-components'
import { PulseFieldLabel } from '@/shared/components/SDComponentsNewDesign/SDFieldLabel/styled-components'

const PdlSearchForm = () => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const { isPulse, fieldText } = useMeterSearchField()
  const {
    getAddress,
    getOccupant,
    getStreetNumber,
    getStreetName,
    getMatricule,
    getSerialNumber,
    getComplement,
    getBuilding,
    getLocality,
    getContractType,
    isAddressFilled,
    isLoading,
    updatePdlConfigData,
    updateAddressHandler,
  } = usePdlSearchV2()
  const { searchPDLs } = useRetrievePDLsV2()

  return (
    <>
      <SearchModalBody $pulse={isPulse}>
        <AddressSelect
          currentValue={getAddress}
          onChange={updateAddressHandler}
          disabled={isLoading}
          required={isPulse}
          {...fieldText('subscriptionTools:pdl_search.fields.address')}
        />
        <div
          className={css({
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            marginBottom: '1rem',
          })}
        >
          <div
            className={css({
              flex: '1 1 30%',
            })}
          >
            <SDInput
              currentValue={getStreetNumber}
              outerOnchange={value => updatePdlConfigData({ address: { ...getAddress, streetNumber: value } })}
              {...fieldText('subscriptionForm:address_module.street_number')}
            />
          </div>
          <div
            className={css({
              flex: '1 1 70%',
            })}
          >
            <SDInput
              currentValue={getStreetName}
              outerOnchange={value => updatePdlConfigData({ address: { ...getAddress, streetName: value } })}
              {...fieldText('subscriptionForm:address_module.street_name')}
            />
          </div>
        </div>

        <SDFormControl plainLabel>
          <SDInput
            {...fieldText('subscriptionTools:pdl_search.fields.occupant')}
            currentValue={getOccupant}
            onBlur={value => updatePdlConfigData({ occupant: value || '' })}
            disabled={isLoading}
          />
        </SDFormControl>

        <SDFormControl plainLabel>
          <SDInput
            {...fieldText('subscriptionTools:pdl_search.fields.complement')}
            currentValue={getComplement}
            onBlur={value => updatePdlConfigData({ complement: value || '' })}
            disabled={isLoading}
          />
        </SDFormControl>

        <div className={css({ display: 'flex', gap: '0.5rem' })}>
          <div className={css({ flex: 1 })}>
            <SDInput
              {...fieldText('subscriptionTools:pdl_search.fields.building')}
              currentValue={getBuilding}
              onBlur={value => updatePdlConfigData({ building: value || '' })}
              disabled={isLoading}
            />
          </div>
          <div className={css({ flex: 1 })}>
            <SDInput
              {...fieldText('subscriptionTools:pdl_search.fields.locality')}
              currentValue={getLocality}
              onBlur={value => updatePdlConfigData({ locality: value })}
              disabled={isLoading}
            />
          </div>
        </div>

        <div
          className={css({
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1rem',
          })}
        >
          <div className={css({ flex: 1 })}>
            <SDInput
              {...fieldText('subscriptionTools:pdl_search.fields.serial_number')}
              currentValue={getSerialNumber}
              onBlur={value => updatePdlConfigData({ serialNumber: value || '' })}
              disabled={isLoading}
            />
          </div>

          <div className={css({ flex: 1 })}>
            <SDInput
              {...fieldText('subscriptionTools:pdl_search.fields.matricule')}
              currentValue={getMatricule}
              onBlur={value => updatePdlConfigData({ matricule: value || '' })}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className={css({ marginTop: '1rem' })}>
          <PulseFieldLabel hidden={!isPulse}>{t('subscriptionForm:customer_module.client_type.label')}</PulseFieldLabel>
          <SDButtonGroup size={SIZE.mini} selected={getContractType === 'pro' ? [1] : [0]} disabled={isLoading}>
            <SDButtonGroupButton onClick={() => updatePdlConfigData({ contractType: 'part' })}>
              {t('subscriptionTools:pdl_search.fields.contract_type_part')}
            </SDButtonGroupButton>
            <SDButtonGroupButton onClick={() => updatePdlConfigData({ contractType: 'pro' })}>
              {t('subscriptionTools:pdl_search.fields.contract_type_pro')}
            </SDButtonGroupButton>
          </SDButtonGroup>
        </div>
      </SearchModalBody>

      <SearchModalFooter $pulse={isPulse}>
        <ModalButton
          id='button-start-search'
          overrides={isPulse ? pulseModalButtonOverrides : undefined}
          endEnhancer={() => <ArrowRight size={isPulse ? 18 : 24} />}
          disabled={!isAddressFilled || isLoading}
          onClick={searchPDLs}
        >
          {t('subscriptionTools:pdl_search.start_search')}
        </ModalButton>
        {isLoading && <Spinner size={24} />}
      </SearchModalFooter>
    </>
  )
}

export default PdlSearchForm
